import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box, Typography, TextField, Stack, FormControl, InputLabel, Select, MenuItem,
  Button, Tooltip, CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CertificateAutofill from './CertificateAutofill';
import { certificateFileError } from '../services/certificatePrefillService';
import { supabase } from '../supabaseClient.js';
import useAlert from '../alert/useAlert.js';

import { DIVISION_OPTIONS, NUMBER_REQUIRED_ORGS } from '../constants/professionalRegistry.js';

const TEAL = '#29C0BB';
// Registry numbers are stored without surrounding brackets/whitespace
// (HKPCA prints them as "(2017-235)"; MCHK as "S24-0398"); strip those so the RPC's exact match works.
const normalizeDivisionNumber = (v) => v.replace(/[()\s]/g, '');

// --- Certificate encryption helpers (same scheme as profile attachments) ---
// deriveKey/encryptFile are exported for other uploads that follow the same
// per-user encryption pattern (e.g. onboarding supporting documents).
async function deriveIV(filename, key) {
  const exportedKey = await crypto.subtle.exportKey('raw', key);
  const keyArray = new Uint8Array(exportedKey);
  const keyHex = Array.from(keyArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const enc = new TextEncoder();
  const data = enc.encode(filename + keyHex);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer).slice(0, 12);
}

export async function deriveKey(patientId) {
  const enc = new TextEncoder();
  const passwordBytes = enc.encode(patientId + process.env.REACT_APP_ENCRYPTION_SECRET);
  const keyMaterial = await crypto.subtle.importKey('raw', passwordBytes, { name: 'PBKDF2' }, false, ['deriveKey']);
  // Salt must match the original Update Profile scheme exactly, or
  // certificates uploaded before this refactor stop decrypting.
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(patientId + 'tikcare_salt'), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  return key;
}

export async function encryptFile(file, key, customFilename = null) {
  const nameToUse = customFilename || file.name;
  const iv = await deriveIV(nameToUse, key);
  const buffer = await file.arrayBuffer();
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, buffer);
  return new Blob([encrypted]);
}

async function decryptFile(encryptedBlob, key, filename) {
  try {
    const iv = await deriveIV(filename, key);
    const buffer = await encryptedBlob.arrayBuffer();
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, buffer);
    const ext = filename.split('.').pop().toLowerCase();
    const mime = ext === 'pdf' ? 'application/pdf' : ext === 'png' ? 'image/png' : 'image/jpeg';
    return new Blob([decrypted], { type: mime });
  } catch (err) {
    console.error('Decrypt failed:', err);
    return encryptedBlob;
  }
}

/**
 * Registry verification block, shared by onboarding step 4 and Settings →
 * Update Profile. Self-contained: loads its fields from user_accounts and
 * persists through the same paths as before (verify/unverify RPCs, direct
 * updates for certificate url + expiry). Parent components that gate features
 * on verification receive updates via onStatusChange(verified).
 */
const ProfessionalVerificationForm = forwardRef(({ userid, username, useremail, onStatusChange, onAutofillStateChange }, ref) => {
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState(null);
  const [org, setOrg] = useState('');
  const [divisionNumber, setDivisionNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verified, setVerified] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [viewingCertificate, setViewingCertificate] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [certificateFile, setCertificateFile] = useState(null);
  const certificateValues = { first_name: firstName, last_name: lastName, registered_number: divisionNumber, certificate_expiry_date: expiryDate };
  const certificateState = useRef(null);
  certificateState.current = { userid, org, verified, updating, values: certificateValues };

  const notifyStatus = useCallback((value) => {
    if (onStatusChange) onStatusChange(value);
  }, [onStatusChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    setCertificateFile(null);
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('user_accounts')
          .select('registered_number, registered_number_org, registered_number_verified, certificate_url, certificate_first_name, certificate_last_name, certificate_expiry_date')
          .eq('id', userid)
          .maybeSingle();
        if (!active) return;
        if (error) throw error;
        setOrg(data?.registered_number_org || '');
        setDivisionNumber(data?.registered_number || '');
        setFirstName(data?.certificate_first_name || '');
        setLastName(data?.certificate_last_name || '');
        const isVerified = Boolean(data?.registered_number_verified);
        setVerified(isVerified);
        notifyStatus(isVerified);
        setCertificateUrl(data?.certificate_url || '');
        setExpiryDate(data?.certificate_expiry_date || '');
        setLoadedUserId(userid);
      } catch (_) {
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [userid, notifyStatus]);

  const autofillLocked = loading || loadError || verified || updating || loadedUserId !== userid ||
    Boolean(org && !DIVISION_OPTIONS.some((option) => option.value === org));
  const autofillState = {
    loading,
    locked: autofillLocked,
    association: DIVISION_OPTIONS.find((option) => option.value === org)?.label || '',
    first_name: firstName,
    last_name: lastName,
    registered_number: divisionNumber,
  };
  const autofillStateRef = useRef(autofillState);
  autofillStateRef.current = autofillState;
  useEffect(() => {
    onAutofillStateChange?.({
      loading, locked: autofillLocked,
      association: DIVISION_OPTIONS.find((option) => option.value === org)?.label || '',
      first_name: firstName, last_name: lastName, registered_number: divisionNumber,
    });
  }, [onAutofillStateChange, loading, autofillLocked, org, firstName, lastName, divisionNumber]);

  useImperativeHandle(ref, () => {
    const setters = {
      association: (value) => setOrg(DIVISION_OPTIONS.find((option) => option.label === value)?.value || ''),
      first_name: setFirstName, last_name: setLastName, registered_number: setDivisionNumber,
    };
    return {
      applyAutofill(rows) {
        const state = autofillStateRef.current;
        if (state.locked) return [];
        const associationRow = (rows || []).find((row) => row.key === 'association');
        const proposed = associationRow?.value;
        const validProposed = DIVISION_OPTIONS.some((option) => option.label === proposed);
        if (proposed && !validProposed) return [];
        const switching = validProposed && state.association !== proposed;
        const snapshot = associationRow?.registrySnapshot;
        if (switching && ((state.association && !snapshot) || (snapshot && Object.keys(setters).some(key => (state[key] || '') !== (snapshot[key] || ''))))) return [];
        const association = validProposed ? proposed : state.association;
        const previousRegistry = switching && state.association ? { ...state } : null;
        if (previousRegistry) {
          for (const key of ['first_name', 'last_name', 'registered_number']) {
            state[key] = '';
            setters[key]('');
          }
        }
        const applied = [];
        for (const row of rows || []) {
          if (!setters[row.key] || typeof row.value !== 'string' || !row.value.trim() || (state[row.key]?.trim() && !(row.key === 'association' && switching))) continue;
          if (row.key === 'association' && !validProposed) continue;
          if (row.key !== 'association' && !association) continue;
          if (row.registryAssociation && row.registryAssociation !== association) continue;
          const code = DIVISION_OPTIONS.find((option) => option.label === association)?.value;
          if (row.key === 'registered_number' && !NUMBER_REQUIRED_ORGS.includes(code)) continue;
          const value = row.key === 'registered_number' ? normalizeDivisionNumber(row.value) : row.value.trim();
          if (!value) continue;
          state[row.key] = value;
          setters[row.key](value);
          applied.push({ ...row, value, ...(row.key === 'association' && previousRegistry ? { previousRegistry } : {}) });
        }
        if (previousRegistry) {
          const registryRow = applied.find(row => row.key === 'association');
          if (registryRow) registryRow.appliedRegistry = { ...state };
        }
        return applied;
      },
      undoAutofill(rows) {
        const state = autofillStateRef.current;
        if (state.locked) return;
        const switched = (rows || []).find(row => row.previousRegistry);
        if (switched) {
          // Restore the group atomically only if none of its fields were manually changed.
          if (Object.keys(setters).every(key => state[key] === switched.appliedRegistry[key])) {
            for (const key of Object.keys(setters)) {
              state[key] = switched.previousRegistry[key] || '';
              setters[key](state[key]);
            }
          }
          return;
        }
        for (const row of rows || []) {
          if (setters[row.key] && state[row.key] === row.value) {
            state[row.key] = '';
            setters[row.key]('');
          }
        }
      },
    };
  }, []);

  const handleVerify = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setAlert('Please enter your registry first and last name for verification.', 'error');
      return;
    }
    setUpdating(true);
    try {
      // Persist the selected association so it survives even when
      // verification fails or the user leaves before profile save.
      await supabase
        .from('user_accounts')
        .update({ registered_number_org: org })
        .eq('id', userid);
      const { data: verificationStatus, error: rpcError } = await supabase.rpc('verify_hkps_division_member', {
        p_division_name: org,
        p_division_number: divisionNumber.trim() || null,
        p_first_name: firstName.trim() || null,
        p_last_name: lastName.trim() || null,
        p_user_id: userid,
      });
      if (rpcError) throw rpcError;
      const isVerified = verificationStatus === 'SUCCESS';
      setVerified(isVerified);
      notifyStatus(isVerified);
      if (!isVerified) {
        if (verificationStatus === 'ALREADY_CLAIMED') {
          setAlert('This registry credential has already been verified/claimed by another account.', 'error');
        } else {
          setAlert('Credentials not verified in the selected Psychological Association / Psychiatrist registry. You can still submit and we will manually review it.', 'error');
        }
      } else {
        setAlert('Credentials verified successfully.', 'success');
      }
    } catch (error) {
      console.error('Error verifying registered number:', error);
      setAlert('Unable to verify credentials. Please try again.', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleUnverify = async () => {
    setUpdating(true);
    try {
      const { data: success, error: rpcError } = await supabase.rpc('unverify_hkps_division_member', {
        p_user_id: userid,
      });
      if (rpcError) throw rpcError;
      if (success) {
        setOrg('');
        setDivisionNumber('');
        setFirstName('');
        setLastName('');
        setVerified(false);
        notifyStatus(false);
        setCertificateUrl('');
        setCertificateFile(null);
        setExpiryDate('');
        setAlert('Credentials unverified successfully.', 'success');
      }
    } catch (error) {
      console.error('Error unverifying credentials:', error);
      setAlert('Unable to unverify credentials. Please try again.', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCertificateUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const invalid = certificateFileError(file);
    if (invalid) { setAlert(invalid, 'error'); return; }
    setCertificateFile(null);
    setUploadingCertificate(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userid}/certificates/${fileName}`;
      const key = await deriveKey(userid);
      const encryptedBlob = await encryptFile(file, key, fileName);

      const { data: existingFiles, error: listError } = await supabase.storage
        .from('tikcare-attachments')
        .list(`${userid}/certificates`);
      if (listError) throw listError;
      for (const existing of existingFiles) {
        await supabase.storage
          .from('tikcare-attachments')
          .remove([`${userid}/certificates/${existing.name}`]);
      }

      const { error: uploadError } = await supabase.storage
        .from('tikcare-attachments')
        .upload(filePath, encryptedBlob, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('tikcare-attachments')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData.publicUrl;

      const { error: dbError } = await supabase
        .from('user_accounts')
        .update({ certificate_url: publicUrl })
        .eq('id', userid);
      if (dbError) throw dbError;

      setCertificateUrl(publicUrl);
      if (certificateState.current.userid === userid) setCertificateFile(file);
      setAlert('Certificate uploaded and recorded successfully.', 'success');
    } catch (error) {
      console.error('Error uploading certificate:', error);
      setAlert('Failed to upload certificate. Please try again.', 'error');
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleExpiryDateChange = async (dateValue) => {
    const formattedDate = dateValue || null;
    try {
      const { error } = await supabase
        .from('user_accounts')
        .update({ certificate_expiry_date: formattedDate, expiry_email_sent: false })
        .eq('id', userid);
      if (error) throw error;
      setExpiryDate(formattedDate || '');
      setAlert('Certificate expiration date updated successfully.', 'success');
      return true;
    } catch (error) {
      console.error('Error updating expiration date:', error);
      setAlert('Unable to save expiration date. Please try again.', 'error');
      return false;
    }
  };

  const applyCertificateDetails = async (fields, snapshot) => {
    const state = certificateState.current;
    if (autofillLocked || state.verified || state.updating ||
        Object.keys(fields).some(key => (state.values[key] || '') !== (snapshot[key] || ''))) return false;
    if (fields.certificate_expiry_date && !await handleExpiryDateChange(fields.certificate_expiry_date)) return false;
    // Saving a reviewed date is asynchronous; do not apply credentials to a changed form/account.
    const latest = certificateState.current;
    if (latest.userid !== state.userid || latest.org !== state.org || latest.verified || latest.updating) return false;
    if (fields.first_name && latest.values.first_name === snapshot.first_name) setFirstName(fields.first_name);
    if (fields.last_name && latest.values.last_name === snapshot.last_name) setLastName(fields.last_name);
    if (fields.registered_number && latest.values.registered_number === snapshot.registered_number) setDivisionNumber(normalizeDivisionNumber(fields.registered_number));
    return true;
  };

  const handleCertificateView = async () => {
    if (!certificateUrl) return;
    try {
      setViewingCertificate(true);
      const urlParts = certificateUrl.split('/');
      const filename = urlParts.pop();
      urlParts.pop(); // 'certificates' folder
      const folderUserid = urlParts.pop();
      const filePath = `${folderUserid}/certificates/${filename}`;
      const { data: encryptedBlob, error: downloadError } = await supabase.storage
        .from('tikcare-attachments')
        .download(filePath);
      if (downloadError) throw downloadError;
      const key = await deriveKey(userid);
      const decryptedBlob = await decryptFile(encryptedBlob, key, filename);
      const blobUrl = URL.createObjectURL(decryptedBlob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Error viewing certificate:', error);
    } finally {
      setViewingCertificate(false);
    }
  };

  const handleHelpContact = () => {
    const subject = encodeURIComponent(`Registry Qualification Verification Help - ${username || useremail}`);
    const body = encodeURIComponent(
      `Hi TikCare Support team,\n\n` +
      `Please help me verify my professional registry credentials. Here is my qualification information for verification:\n\n` +
      `- Psychological Association / Psychiatrist Registry: ${org || '(Not selected)'}\n` +
      `- First and Middle Name on Registry: ${firstName || '(Not entered)'}\n` +
      `- Last Name on Registry: ${lastName || '(Not entered)'}\n` +
      `- ${org === 'MCHK' ? 'MCHK Registration No.' : org === 'HKPCA' ? 'CCoun No.' : 'Division Number'}: ${divisionNumber || '(Not entered)'}\n\n` +
      `Remainder: Please provide your certificate as an attachment.\n\n` +
      `Best regards,\n` +
      `${username || 'User'}`
    );
    window.location.href = `mailto:info@tikcare.co?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return <CircularProgress size={22} sx={{ color: TEAL }} />;
  }

  const verifyDisabled =
    verified || updating || !firstName.trim() || !lastName.trim() ||
    !DIVISION_OPTIONS.some((o) => o.value === org) ||
    (NUMBER_REQUIRED_ORGS.includes(org) && !divisionNumber.trim());

  return (
    <Box id="registered-number-section" sx={{ width: '100%' }}>
      <FormControl fullWidth size="small" sx={{ mb: 0.5 }} disabled={verified}>
        <InputLabel id="reg-org-label">Psychological Association / Psychiatrist Registry</InputLabel>
        <Select
          labelId="reg-org-label"
          value={org}
          label="Psychological Association / Psychiatrist Registry"
          onChange={(e) => {
            const val = e.target.value;
            setOrg(val);
            if (!NUMBER_REQUIRED_ORGS.includes(val)) {
              setDivisionNumber('');
            }
          }}
        >
          {DIVISION_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, mt: 1.5 }}>
        Your Name (Please use exact same name on your division member list)
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="First and Middle Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          sx={{ backgroundColor: '#f5f5f5', flex: 1 }}
          disabled={verified}
        />
        <TextField
          size="small"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          sx={{ backgroundColor: '#f5f5f5', flex: 1 }}
          disabled={verified}
        />
      </Stack>

      {NUMBER_REQUIRED_ORGS.includes(org) && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {org === 'MCHK' ? 'MCHK Registration No.' : org === 'HKPCA' ? 'CCoun No.' : 'Division Number'}
            </Typography>
            <Tooltip title={org === 'MCHK'
              ? 'Enter your Specialist Register registration number as shown on the MCHK list, e.g. S24-0398.'
              : org === 'HKPCA'
              ? 'Enter the Certified Counsellor number printed on the HKPCA list, e.g. (2017-235). Brackets are optional.'
              : 'Enter your division number'}>
              <InfoOutlinedIcon fontSize="small" sx={{ color: TEAL }} />
            </Tooltip>
          </Box>
          <TextField
            size="small"
            value={divisionNumber}
            onChange={(e) => setDivisionNumber(normalizeDivisionNumber(e.target.value))}
            placeholder={org === 'MCHK' ? 'e.g., S24-0398' : org === 'HKPCA' ? 'e.g., 2017-235 or 2026-1138' : 'e.g., DEP0XXX, CP00XXX, AEP00XX'}
            sx={{ width: 300, backgroundColor: '#f5f5f5', mb: 2 }}
            disabled={verified}
          />
        </>
      )}

      {/* Certificate Upload Section */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Registration Certificate</Typography>
          <Tooltip title="Upload your professional registration certificate (PDF, JPG, PNG) for record.">
            <InfoOutlinedIcon fontSize="small" sx={{ color: TEAL }} />
          </Tooltip>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Button
            variant="outlined"
            component="label"
            size="small"
            sx={{
              textTransform: 'none',
              borderColor: TEAL,
              color: TEAL,
              '&:hover': { borderColor: '#1da8a3', backgroundColor: '#e0f2f1' },
            }}
          >
            {uploadingCertificate ? 'Uploading...' : 'Upload Certificate'}
            <input
              type="file"
              aria-label="Upload registration certificate"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleCertificateUpload}
              disabled={uploadingCertificate}
            />
          </Button>
          <TextField
            label="Expiration Date"
            type="date"
            size="small"
            value={expiryDate}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => handleExpiryDateChange(e.target.value)}
            sx={{ width: 170 }}
          />
          {uploadingCertificate && <CircularProgress size={20} sx={{ color: TEAL }} />}
        </Stack>
        <Typography variant="caption" sx={{ color: '#8a94a0', display: 'block', mt: 0.5 }}>
          PDF, JPG or PNG (Max 10MB)
        </Typography>
        <CertificateAutofill file={certificateFile} registry={org} userid={userid}
          locked={autofillLocked || uploadingCertificate} values={certificateValues} onApply={applyCertificateDetails} />
        {certificateUrl && (
          <Typography variant="caption" display="block" sx={{ mt: 1, color: '#1da8a3' }}>
            Certificate uploaded:{' '}
            <button
              onClick={handleCertificateView}
              disabled={viewingCertificate}
              style={{
                background: 'none',
                border: 'none',
                color: TEAL,
                padding: 0,
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: viewingCertificate ? 'wait' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit',
              }}
            >
              {viewingCertificate ? 'Viewing...' : 'View Certificate'}
            </button>
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
        {verified ? (
          <Button
            variant="contained"
            onClick={handleUnverify}
            disabled={updating}
            sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' }, textTransform: 'none' }}
          >
            {updating ? 'Processing...' : 'Unverify'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verifyDisabled}
            sx={{ backgroundColor: TEAL, '&:hover': { backgroundColor: '#1da8a3' }, textTransform: 'none' }}
          >
            {updating ? 'Verifying...' : 'Verify'}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleHelpContact}
          sx={{
            borderColor: TEAL,
            color: TEAL,
            '&:hover': { borderColor: '#1da8a3', backgroundColor: '#e0f2f1' },
            textTransform: 'none',
          }}
        >
          Help
        </Button>
      </Box>

      <Typography variant="caption" sx={{ mt: 1, color: verified ? '#1da8a3' : '#f44336' }}>
        {verified ? 'Registered number verified.' : 'Registered number not verified.'}
      </Typography>
    </Box>
  );
});

export default ProfessionalVerificationForm;
